import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { invalidateCampaignQueries } from '#/entities/campaign'
import {
  useAssignPeopleApiV1CampaignsCampaignIdPeopleAssignPost,
  useCreatePeopleTasksApiV1CampaignsCampaignIdPeopleTasksPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import type { CampaignPeopleTasksResult } from '#/shared/api/generated/communication/models'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

/**
 * Task kinds `POST /campaigns/{id}/people/tasks` accepts. The service
 * documents them on the body's `type` field as plain strings rather than an
 * enum — `call`, `email`, `todo` — and maps them onto the shipment service's
 * own task types itself.
 */
export const CAMPAIGN_TASK_TYPES = {
  call: 'call',
  email: 'email',
  todo: 'todo',
} as const

export type CampaignTaskType =
  (typeof CAMPAIGN_TASK_TYPES)[keyof typeof CAMPAIGN_TASK_TYPES]

export const CAMPAIGN_TASK_TYPE_ORDER: readonly CampaignTaskType[] = [
  CAMPAIGN_TASK_TYPES.call,
  CAMPAIGN_TASK_TYPES.email,
  CAMPAIGN_TASK_TYPES.todo,
]

export interface CampaignTasksInput {
  title: string
  type: CampaignTaskType
  /** `YYYY-MM-DD`, or null to leave the tasks undated. */
  date: string | null
}

/** Created / skipped / failed are three different outcomes, and the toast
 *  says each of them — a "5 created" that hides "12 failed" is a lie. */
function toastTasksResult(t: TFunction, result: CampaignPeopleTasksResult) {
  const notes = [
    result.skipped > 0
      ? t('campaigns.contact.toasts.tasksSkipped', { count: result.skipped })
      : null,
    result.failed > 0
      ? t('campaigns.contact.toasts.tasksFailed', { count: result.failed })
      : null,
  ].filter((note) => note != null)
  const message = t('campaigns.contact.toasts.tasksCreated', {
    count: result.created,
  })
  if (result.failed > 0) {
    toast.error(message, { description: notes.join(' · ') })
    return
  }
  toast.success(message, {
    description: notes.length > 0 ? notes.join(' · ') : undefined,
  })
}

/**
 * The two bulk-bar actions that need an answer before they run — an agent, a
 * task — so each opens a dialog and lands here on submit. Both take the
 * ticked enrollment ids (the contact row's `id` *is* the enrollment id) and
 * clear the selection once the service has answered.
 */
export function useCampaignPeopleActions(
  campaignId: string,
  onDone: () => void,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const fail = useCallback(
    (error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    [t],
  )
  const settle = useCallback(() => {
    invalidateCampaignQueries(queryClient)
    onDone()
  }, [queryClient, onDone])

  const assign = useAssignPeopleApiV1CampaignsCampaignIdPeopleAssignPost({
    mutation: {
      onSuccess: (response) => {
        toast.success(
          t('campaigns.contact.toasts.assigned', {
            count: response.data.assigned,
          }),
        )
        settle()
      },
      onError: fail,
    },
  })

  const createTasks =
    useCreatePeopleTasksApiV1CampaignsCampaignIdPeopleTasksPost({
      mutation: {
        onSuccess: (response) => {
          toastTasksResult(t, response.data)
          settle()
        },
        onError: fail,
      },
    })

  const onAssign = useCallback(
    (enrollmentIds: string[], userId: string) =>
      assign.mutate({
        campaignId,
        data: { enrollment_ids: enrollmentIds, user_id: userId },
      }),
    [assign, campaignId],
  )

  const onCreateTasks = useCallback(
    (enrollmentIds: string[], input: CampaignTasksInput) =>
      createTasks.mutate({
        campaignId,
        data: {
          enrollment_ids: enrollmentIds,
          title: input.title.trim(),
          type: input.type,
          date: input.date,
        },
      }),
    [createTasks, campaignId],
  )

  return {
    onAssign,
    isAssigning: assign.isPending,
    onCreateTasks,
    isCreatingTasks: createTasks.isPending,
  }
}
